'use client';
import React from 'react';
import { useForm } from 'react-hook-form';
import { MapPin, Phone, User, Navigation, ArrowRight } from 'lucide-react';
import type { DeliveryFormData } from './BookDeliveryWizard';

const DAR_AREAS = [
  'Kariakoo, Dar es Salaam',
  'Kinondoni, Dar es Salaam',
  'Ilala, Dar es Salaam',
  'Temeke, Dar es Salaam',
  'Msasani, Dar es Salaam',
  'Mikocheni, Dar es Salaam',
  'Mwananyamala, Dar es Salaam',
  'Tabata, Dar es Salaam',
  'Buguruni, Dar es Salaam',
  'Gongo la Mboto, Dar es Salaam',
  'Mbagala, Dar es Salaam',
  'Kigamboni, Dar es Salaam',
];

interface StepLocationsProps {
  formData: DeliveryFormData;
  updateForm: (partial: Partial<DeliveryFormData>) => void;
  onNext: () => void;
}

interface LocationFormValues {
  pickupAddress: string;
  pickupLandmark: string;
  pickupPhone: string;
  deliveryAddress: string;
  deliveryLandmark: string;
  deliveryPhone: string;
  recipientName: string;
}

export default function StepLocations({ formData, updateForm, onNext }: StepLocationsProps) {
  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<LocationFormValues>({
    defaultValues: {
      pickupAddress: formData.pickupAddress,
      pickupLandmark: formData.pickupLandmark,
      pickupPhone: formData.pickupPhone,
      deliveryAddress: formData.deliveryAddress,
      deliveryLandmark: formData.deliveryLandmark,
      deliveryPhone: formData.deliveryPhone,
      recipientName: formData.recipientName,
    },
  });

  const onSubmit = (data: LocationFormValues) => {
    // BACKEND INTEGRATION: POST /api/distance/calculate with pickup + delivery coords
    const mockDistance = 4.2 + Math.random() * 12;
    updateForm({
      ...data,
      distanceKm: parseFloat(mockDistance.toFixed(1)),
    });
    onNext();
  };

  const useCurrentLocation = () => {
    setValue('pickupAddress', 'Kariakoo, Dar es Salaam');
    setValue('pickupLandmark', 'Kariakoo Market, near Mosque');
    setValue('pickupPhone', '+255 754 123 456');
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      {/* Pickup */}
      <div className="card-elevated p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
            <Navigation size={16} className="text-green-600" />
          </div>
          <h3 className="text-base font-semibold text-foreground">Mahali pa Kuchukua (Pickup)</h3>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1 tracking-wide">
              Anwani ya Pickup <span className="text-red-500">*</span>
            </label>
            <p className="text-[11px] text-muted-foreground mb-1.5">Mtaa, Jiji — e.g. Kariakoo, Dar es Salaam</p>
            <div className="relative">
              <MapPin size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                {...register('pickupAddress', { required: 'Tafadhali ingiza anwani ya pickup' })}
                list="dar-areas-pickup"
                placeholder="e.g. Kariakoo, Dar es Salaam"
                className={`input-base w-full pl-9 pr-4 py-2.5 text-sm ${errors.pickupAddress ? 'input-error' : ''}`}
              />
              <datalist id="dar-areas-pickup">
                {DAR_AREAS.map(a => <option key={`pickup-area-${a}`} value={a} />)}
              </datalist>
            </div>
            {errors.pickupAddress && <p className="text-xs text-red-600 mt-1">{errors.pickupAddress.message}</p>}
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground mb-1 tracking-wide">Alama ya Karibu (Landmark)</label>
            <input
              {...register('pickupLandmark')}
              placeholder="e.g. Kariakoo Market, karibu na Msikiti"
              className="input-base w-full px-4 py-2.5 text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground mb-1 tracking-wide">
              Nambari ya Simu ya Pickup <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Phone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                {...register('pickupPhone', {
                  required: 'Tafadhali ingiza nambari ya simu',
                  pattern: { value: /^\+?255\s?\d{9}$|^0\d{9}$/, message: 'Nambari si sahihi — e.g. +255 754 000 000' }
                })}
                placeholder="+255 754 000 000"
                className={`input-base w-full pl-9 pr-4 py-2.5 text-sm ${errors.pickupPhone ? 'input-error' : ''}`}
              />
            </div>
            {errors.pickupPhone && <p className="text-xs text-red-600 mt-1">{errors.pickupPhone.message}</p>}
          </div>

          <button
            type="button"
            onClick={useCurrentLocation}
            className="flex items-center gap-1.5 text-xs text-primary font-semibold hover:underline"
          >
            <Navigation size={12} />
            Tumia Eneo Langu la Sasa (Use My Location)
          </button>
        </div>
      </div>

      {/* Arrow */}
      <div className="flex justify-center">
        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
          <ArrowRight size={16} className="text-primary" />
        </div>
      </div>

      {/* Delivery */}
      <div className="card-elevated p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
            <MapPin size={16} className="text-red-600" />
          </div>
          <h3 className="text-base font-semibold text-foreground">Mahali pa Kupeleka (Delivery)</h3>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1 tracking-wide">
              Anwani ya Delivery <span className="text-red-500">*</span>
            </label>
            <p className="text-[11px] text-muted-foreground mb-1.5">Mtaa, Jiji — e.g. Msasani, Dar es Salaam</p>
            <div className="relative">
              <MapPin size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                {...register('deliveryAddress', { required: 'Tafadhali ingiza anwani ya delivery' })}
                list="dar-areas-delivery"
                placeholder="e.g. Msasani, Dar es Salaam"
                className={`input-base w-full pl-9 pr-4 py-2.5 text-sm ${errors.deliveryAddress ? 'input-error' : ''}`}
              />
              <datalist id="dar-areas-delivery">
                {DAR_AREAS.map(a => <option key={`delivery-area-${a}`} value={a} />)}
              </datalist>
            </div>
            {errors.deliveryAddress && <p className="text-xs text-red-600 mt-1">{errors.deliveryAddress.message}</p>}
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground mb-1 tracking-wide">Alama ya Karibu (Landmark)</label>
            <input
              {...register('deliveryLandmark')}
              placeholder="e.g. karibu na Mlimani City Mall"
              className="input-base w-full px-4 py-2.5 text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground mb-1 tracking-wide">
              Jina la Mpokeaji <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <User size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                {...register('recipientName', { required: 'Tafadhali ingiza jina la mpokeaji' })}
                placeholder="Jina Kamili la Mpokeaji"
                className={`input-base w-full pl-9 pr-4 py-2.5 text-sm ${errors.recipientName ? 'input-error' : ''}`}
              />
            </div>
            {errors.recipientName && <p className="text-xs text-red-600 mt-1">{errors.recipientName.message}</p>}
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground mb-1 tracking-wide">
              Nambari ya Simu ya Mpokeaji <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Phone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                {...register('deliveryPhone', {
                  required: 'Tafadhali ingiza nambari ya simu ya mpokeaji',
                  pattern: { value: /^\+?255\s?\d{9}$|^0\d{9}$/, message: 'Nambari si sahihi' }
                })}
                placeholder="+255 754 000 000"
                className={`input-base w-full pl-9 pr-4 py-2.5 text-sm ${errors.deliveryPhone ? 'input-error' : ''}`}
              />
            </div>
            {errors.deliveryPhone && <p className="text-xs text-red-600 mt-1">{errors.deliveryPhone.message}</p>}
          </div>
        </div>
      </div>

      <button
        type="submit"
        className="w-full btn-primary py-3.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2"
      >
        Endelea — Aina ya Kifurushi
        <ArrowRight size={16} />
      </button>
    </form>
  );
}